<template>
  <div id="map" class="h-96 w-full rounded-lg"></div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { computed, watch, onMounted, createApp, h } from 'vue';
import DashboardMapPopup from './DashboardMapPopup.vue';
import { sortMapPins } from '@/models/building';

export default {
  name: 'DashboardMap',
  props: {
    buildings: {
      type: Array
    }
  },
  setup(props, { emit }) {
    let map = null;
    let markers = [];
    const buildings = computed(() => props.buildings ?? []);

    onMounted(() => {
      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_KEY;
      map = new mapboxgl.Map({
        container: 'map',
        style: 'mapbox://styles/mapbox/light-v10',
        center: [-80.213008, 43.495004],
        zoom: 9,
        maxZoom: 15
      });
      updateMapWithNewBuildings(buildings.value);
    });

    watch(buildings, (newBuildings) => {
      updateMapWithNewBuildings(newBuildings);
    });

    function updateMapWithNewBuildings(newBuildings) {
      markers.forEach(marker => marker.remove());
      markers = [];
      const bounds = new mapboxgl.LngLatBounds();
      const newBuildingsWithCoordinates = newBuildings
        .filter(building => building.coordinate != null);
      newBuildingsWithCoordinates
        .sort(sortMapPins)
        .forEach(building => extendBoundsAndAddMarker(bounds, building));
      if (newBuildingsWithCoordinates.length > 0) {
        map.fitBounds(bounds, createMapOptions(newBuildingsWithCoordinates.length));
      }
    }

    function extendBoundsAndAddMarker(bounds, building) {
      bounds.extend(building.coordinate);
      const marker = addMarker(building, map);
      markers.push(marker);
    }

    function addMarker(building, map) {
      const element = document.createElement('div');
      element.className = `marker ${building.risk.markerColor}`;
      return new mapboxgl.Marker(element)
        .setLngLat(building.coordinate)
        .setPopup(createPopup(createPopupComponent(element, building)))
        .addTo(map);
    }

    function createPopupComponent(element, building) {
      return createApp({
        setup() {
          return () => h(DashboardMapPopup, {
            building: building,
            onGoToDetail: () => emit('go-to-detail', building)
          });
        }
      }).mount(element);
    }

    function createPopup(popupComponent) {
      return new mapboxgl.Popup({ offset: 25, closeButton: false })
        .setDOMContent(popupComponent.$el)
        .setMaxWidth('280px');
    }

    function createMapOptions(newBuildingsLength) {
      let options = { padding: 40 };
      if (newBuildingsLength === 1) {
        options['maxZoom'] = 11;
      }
      return options;
    }

    return {
      map,
      buildings
    };
  },
};
</script>

<style>
.mapboxgl-marker {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 4px solid white;
}

.mapboxgl-popup-content {
  font: 400 15px/22px 'Inter', sans-serif;
  background-color: #fbfbfb;
  border-radius: 5px;
  min-width: 280px;
  padding: 0.5rem
}

.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
  border-top-color: #fbfbfb;
}
</style>